/**
 * One place that turns a deal's sync_status into what the user sees.
 *
 * The label and badge-variant logic used to be written out three times — in
 * the deal detail page, the HubSpot modal, and the deals table — and had
 * drifted apart: the table rendered the raw status string ("not_synced"), the
 * page rendered nothing at all for a failed deal, and only the modal spelled
 * the statuses out. Anything that shows a sync status should call this.
 */

import type { SyncStatus } from '../types/api';

export type BadgeVariant = 'default' | 'secondary' | 'destructive' | 'outline';

export interface SyncStatusPresentation {
  label: string;
  variant: BadgeVariant;
  /** True when the last sync attempt failed, so callers can surface the error. */
  isFailed: boolean;
}

/**
 * Optional context for deals whose real state isn't captured by sync_status
 * alone. A deal billed as a standalone invoice has no subscription, so its
 * sync_status stays "not_synced" even though something real is attached.
 */
export interface SyncStatusContext {
  /** A subscription is genuinely attached (synced or user-confirmed link). */
  isSubscriptionAttached?: boolean;
  isInvoiceLinked?: boolean;
  isInvoiceBilled?: boolean;
}

const BASE: Record<SyncStatus, { label: string; variant: BadgeVariant }> = {
  synced: { label: 'Synced', variant: 'default' },
  linked: { label: 'Linked', variant: 'default' },
  failed: { label: 'Failed', variant: 'destructive' },
  pending: { label: 'Pending', variant: 'secondary' },
  awaiting_selection: { label: 'Needs review', variant: 'secondary' },
  not_synced: { label: 'Not synced', variant: 'outline' },
  unavailable: { label: 'Something went wrong', variant: 'destructive' },
};

const FALLBACK = { label: 'Not synced', variant: 'outline' as BadgeVariant };

/** Statuses that describe the subscription itself, so no invoice fallback applies. */
const SUBSCRIPTION_STATUSES: ReadonlySet<string> = new Set([
  'synced',
  'linked',
  'failed',
  // 'unavailable' means the row itself could not be built, so its invoice
  // fields are as unreadable as everything else on it. Relabelling it
  // "Synced (invoice)" would dress a broken row up as a healthy one.
  'unavailable',
]);

export function describeSyncStatus(
  syncStatus: SyncStatus | string | null | undefined,
  context: SyncStatusContext = {},
): SyncStatusPresentation {
  const { isSubscriptionAttached, isInvoiceLinked, isInvoiceBilled } = context;
  const base = BASE[syncStatus as SyncStatus] ?? FALLBACK;
  // Both states are bad ones the user is owed an explanation for, and every
  // caller uses this flag to decide whether to surface the reason — the
  // table's tooltip, the deal detail's alert, the HubSpot modal's alert. The
  // label is what tells them apart: a sync that failed, versus a row the
  // server could not build.
  const isFailed = syncStatus === 'failed' || syncStatus === 'unavailable';

  // With no subscription state of its own, fall back to the invoice side so an
  // invoice-only deal doesn't read as "Not synced".
  if (!SUBSCRIPTION_STATUSES.has(String(syncStatus))) {
    if (isInvoiceLinked) {
      return { label: 'Linked (invoice)', variant: 'default', isFailed };
    }
    if (isInvoiceBilled) {
      return { label: 'Synced (invoice)', variant: 'default', isFailed };
    }
  }

  // An attached subscription always reads as attached, whatever the row says.
  if (isSubscriptionAttached) {
    return { label: base.label, variant: 'default', isFailed };
  }

  return { ...base, isFailed };
}
